import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { AppConfig } from "../lib/types";

export default function Audit({ config }: { config: AppConfig | null }) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.audit>> | null>(null);
  const [access, setAccess] = useState<Record<string, string | null>[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    api.audit().then(setData).catch((e: Error) => setErr(e.message));
    api.accessLog().then(setAccess).catch((e: Error) => setErr(e.message));
  }, []);
  useEffect(load, [load]);
  if (err) return <p className="error">{err}</p>;
  if (!data) return <p className="muted">Loading…</p>;
  return (
    <div className="stack">
      {config?.demo_mode && <DemoReset onDone={load} />}
      <section className="card">
        <h2>Override rates by recommendation</h2>
        <p className="small muted">
          High decline rates are the first signal that a rule needs clinical-governance review. Patient identifiers are
          pseudonymised.
        </p>
        <div className="table-scroll">
        <table className="list small">
          <thead>
            <tr><th>Recommendation</th><th>KAS</th><th>Accepted</th><th>Declined</th><th>Decline rate</th></tr>
          </thead>
          <tbody>
            {data.override_rates.map((r) => (
              <tr key={String(r.rec_id)}>
                <td>{r.rec_id}</td><td>{r.kas}</td><td>{r.accepted}</td><td>{r.declined}</td>
                <td>{r.decline_rate == null ? "–" : `${Math.round(Number(r.decline_rate) * 100)}%`}</td>
              </tr>
            ))}
            {data.override_rates.length === 0 && <tr><td colSpan={5} className="muted">No decisions yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </section>
      <section className="card">
        <h2>Decision log</h2>
        <div className="table-scroll">
        <table className="list small">
          <thead>
            <tr><th>When (UTC)</th><th>Patient (pseudonym)</th><th>Clinician</th><th>Recommendation</th><th>Decision</th><th>Reason</th><th>Write-back</th></tr>
          </thead>
          <tbody>
            {data.decisions.map((d, i) => (
              <tr key={i}>
                <td>{d.at}</td><td><code>{d.patient}</code></td><td>{d.clinician}</td><td>{d.rec_id}</td>
                <td>{d.decision}</td><td>{d.reason ?? ""}</td><td>{d.writeback}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </section>
      <section className="card">
        <h2>Access log</h2>
        <p className="small muted">Every sign-in, patient view, AI request and write-back (HIPAA audit controls). Latest 500.</p>
        <div className="table-scroll">
        <table className="list small">
          <thead>
            <tr><th>When (UTC)</th><th>User</th><th>Action</th><th>Patient (pseudonym)</th><th>Detail</th><th>IP</th></tr>
          </thead>
          <tbody>
            {(access ?? []).map((r, i) => (
              <tr key={i}>
                <td>{r.at}</td><td>{r.username}</td><td>{r.action}</td><td><code>{r.patient ?? ""}</code></td>
                <td>{r.detail ?? ""}</td><td>{r.ip ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </section>
    </div>
  );
}

function DemoReset({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const reset = () => {
    if (!window.confirm(t("demoResetConfirm"))) return;
    setBusy(true);
    setMsg(null);
    setErr(null);
    api
      .demoReset()
      .then((r) => {
        setMsg(t("demoResetDone", { patients: r.snapshots_removed, decisions: r.decisions_removed }));
        onDone();
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <section className="card">
      <h2>{t("demoResetTitle")}</h2>
      <p className="small muted">{t("demoResetHelp")}</p>
      <button className="btn" disabled={busy} onClick={reset}>
        {busy ? t("demoResetting") : t("demoResetButton")}
      </button>
      {msg && <p className="notice-ok" role="status">{msg}</p>}
      {err && <p className="error" role="alert">{err}</p>}
    </section>
  );
}
